import React, {useCallback, useEffect, useMemo} from "react";
import {useController, UseFormGetValues, useWatch} from "react-hook-form";
import _, {isEqual} from "lodash";
import {
    BaseFormComponent,
    DerivedComponent,
    FormComponentProps,
    FormType,
    FormValues,
    KeyGridMeta,
    RuleType,
} from "../../DynamicFormTypes.ts";
import {useFormInfo, useFormMethods} from "../../DyanmicFormContext.tsx";
import {useValidationRules} from "../../hooks/useValidationRules.tsx";
import {useThrottled} from "../../hooks/useThrottled.ts";
import { defaultParserForSelects, extractDataGridKey } from "../../dyanimcFormUtil.tsx";
import {FieldInfoResponse, INFO_TYPE} from "../../../../api/ApiTypes.ts";
import {safeParseDate, findKey, getStringValue, hasValue} from "./derivedUtils.ts";
import {millisecondsInDay} from "date-fns/constants";
import {NumberField} from "../NumberField.tsx";

// ─── Calculation ──────────────────────────────────────────────────────────────



const calculateValue = (component: DerivedComponent, getValues: UseFormGetValues<FormValues>): Number | null => {

    const getParsedNumericValue = (key: string | undefined): number => {
        if (!key) return NaN;
        const raw = getValues(key);
        const str = typeof raw === 'string' ? raw : String(raw ?? '');
        return parseFloat(defaultParserForSelects(str)?.value ?? str);
    };

    const getComparableNumericValue = (fullKey: ReturnType<typeof extractDataGridKey>, key: string | undefined): number => {
        if (!key) return NaN;
        const dgValue = getValues(`${fullKey.dataGrid}.${fullKey.row}.${key}`);
        if (hasValue(dgValue)) {
            return getParsedNumericValue(`${fullKey.dataGrid}.${fullKey.row}.${key}`);
        }
        return getParsedNumericValue(key);
    };

    const getDateValue = (fullKey: ReturnType<typeof extractDataGridKey>, key: string | undefined): Date | undefined => {
        if (!key) return undefined;
        const dgValue = getValues(`${fullKey.dataGrid}.${fullKey.row}.${key}`);
        if (hasValue(dgValue)) {
            return safeParseDate(dgValue);
        }
        return safeParseDate(getValues(key));
    }

    switch (component.calculationRule) {

        case RuleType.ADJUSTMENT: {
            const fullKey = extractDataGridKey(component.fieldKey);

            const adjustmentMultiplier = getParsedNumericValue(findKey(KeyGridMeta.ADJUSTMENT_MULTIPLIER, component));
            const subjectAmount = getParsedNumericValue(findKey(KeyGridMeta.SUBJECT_AMOUNT, component));
            const comparableAmount = getComparableNumericValue(fullKey, findKey(KeyGridMeta.COMPARABLE_AMOUNT, component));

            if (!hasValue(adjustmentMultiplier) || !hasValue(subjectAmount) || !hasValue(comparableAmount)) {
                return null;
            }

            return Math.round((subjectAmount - comparableAmount) * adjustmentMultiplier);
        }

        case RuleType.COMBINED_ADJUSTMENT: {
            const fullKey = extractDataGridKey(component.fieldKey);

            const multiplierA = getParsedNumericValue(findKey(KeyGridMeta.ADJUSTMENT_MULTIPLIER_A, component));
            const multiplierB = getParsedNumericValue(findKey(KeyGridMeta.ADJUSTMENT_MULTIPLIER_B, component));
            const subjectA = getParsedNumericValue(findKey(KeyGridMeta.SUBJECT_AMOUNT_A, component));
            const subjectB = getParsedNumericValue(findKey(KeyGridMeta.SUBJECT_AMOUNT_B, component));
            const comparableA = getComparableNumericValue(fullKey, findKey(KeyGridMeta.COMPARABLE_AMOUNT_A, component));
            const comparableB = getComparableNumericValue(fullKey, findKey(KeyGridMeta.COMPARABLE_AMOUNT_B, component));

            if ([multiplierA, multiplierB, subjectA, subjectB, comparableA, comparableB].some(v => !hasValue(v))) {
                return null;
            }

            return Math.round((subjectA - comparableA) * multiplierA + (subjectB - comparableB) * multiplierB);
        }

        case RuleType.BASEMENT_ADJUSTMENT: {
            const typesToConsiderZero = ["None"];

            const fullKey = extractDataGridKey(component.fieldKey);

            const multiplierA = getParsedNumericValue(findKey(KeyGridMeta.ADJUSTMENT_MULTIPLIER_A, component));
            const multiplierB = getParsedNumericValue(findKey(KeyGridMeta.ADJUSTMENT_MULTIPLIER_B, component));
            let subjectA = getParsedNumericValue(findKey(KeyGridMeta.SUBJECT_AMOUNT_A, component));
            let subjectB = getParsedNumericValue(findKey(KeyGridMeta.SUBJECT_AMOUNT_B, component));
            const subjectType = getStringValue(fullKey, findKey(KeyGridMeta.SUBJECT_TYPE, component), getValues);
            if (typesToConsiderZero.includes(subjectType)) {
                subjectA = 0;
                subjectB = 0;
            }
            let comparableA = getComparableNumericValue(fullKey, findKey(KeyGridMeta.COMPARABLE_AMOUNT_A, component));
            let comparableB = getComparableNumericValue(fullKey, findKey(KeyGridMeta.COMPARABLE_AMOUNT_B, component));
            const comparableType = getStringValue(fullKey, findKey(KeyGridMeta.COMPARABLE_TYPE, component), getValues);
            if (typesToConsiderZero.includes(comparableType)) {
                comparableA = 0;
                comparableB = 0;
            }

            if ([multiplierA, multiplierB, subjectA, subjectB, comparableA, comparableB].some(v => !hasValue(v))) {
                return null;
            }

            return Math.round((subjectA - comparableA) * multiplierA + (subjectB - comparableB) * multiplierB);
        }

        case RuleType.GARAGE_ADJUSTMENT: {
            const typesToConsiderZero = ["None"];

            const fullKey = extractDataGridKey(component.fieldKey);

            const multiplier = getParsedNumericValue(findKey(KeyGridMeta.ADJUSTMENT_MULTIPLIER, component));
            let subject = getParsedNumericValue(findKey(KeyGridMeta.SUBJECT_AMOUNT, component));
            const subjectType = getStringValue(fullKey, findKey(KeyGridMeta.SUBJECT_TYPE, component), getValues);
            if (typesToConsiderZero.includes(subjectType)) {
                subject = 0;
            }
            let comparable = getComparableNumericValue(fullKey, findKey(KeyGridMeta.COMPARABLE_AMOUNT, component));
            const comparableType = getStringValue(fullKey, findKey(KeyGridMeta.COMPARABLE_TYPE, component), getValues);
            if (typesToConsiderZero.includes(comparableType)) {
                comparable = 0;
            }

            if ([multiplier, subject, comparable].some(v => !hasValue(v))) {
                return null;
            }

            return Math.round((subject - comparable) * multiplier);
        }

        case RuleType.DAYS_DIFFERENCE: {
            const fullKey = extractDataGridKey(component.fieldKey);

            const laterDate = getDateValue(fullKey, findKey(KeyGridMeta.LATER_DATE, component));
            const earlierDate = getDateValue(fullKey, findKey(KeyGridMeta.EARLIER_DATE, component));

            if (!laterDate?.getTime() || !earlierDate?.getTime()) {
                return null;
            }
            // Normalize to UTC midnight to avoid DST skew
            const toUTCMidnight = (d: Date) =>
                Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());

            const daysDifference = Math.round(
                (toUTCMidnight(laterDate) - toUTCMidnight(earlierDate)) / millisecondsInDay
            );
            return daysDifference;
        }

        case RuleType.NINETY_DAY_AS_REPAIRED: {
            const asIsSalePrice = getParsedNumericValue(findKey(KeyGridMeta.PRICE, component));
            const totalRepairs = getParsedNumericValue(findKey(KeyGridMeta.TOTAL_REPAIRS, component));
            const returnOnInvestment = getParsedNumericValue(findKey(KeyGridMeta.ROI, component));

            if (!asIsSalePrice) {
                return null;
            }

            if (!totalRepairs || !returnOnInvestment) {
                return asIsSalePrice;
            }

            return asIsSalePrice + (totalRepairs * (returnOnInvestment / 100));
        }

        default:
            return null;
    }
};

const fieldToLog = null;
const log = (component: BaseFormComponent) => {
    if (!fieldToLog) {
        return false;
    }
    if (component.fieldKey === fieldToLog) {
        return true;
    }
}
// ─── Component ────────────────────────────────────────────────────────────────

const OverrideableNumberCalculationRenderer: React.FC<FormComponentProps<DerivedComponent>> = ({component}) => {
    const {formMethods} = useFormMethods();
    const {setInfo} = useFormInfo();
    const {control, getValues} = formMethods;

    const validate = useValidationRules({component});
    const {field, fieldState} = useController({name: component.fieldKey, control, rules: validate});

    // isOverridden is derived from the unified info record — persists across unmount/remount
    const convertedInfoKey = `_info.${component.fieldKey.replace(/\./g, "_")}`;
    const infoRecord = useWatch({control, name: convertedInfoKey, defaultValue: {}});
    const isOverridden = useMemo(() => {
        const overridden: boolean = !!(infoRecord as Record<INFO_TYPE, FieldInfoResponse> | undefined)?.[INFO_TYPE.OVERRIDE]
        if (log(component)) {
            console.info(`${fieldToLog} overridden: ${overridden} infoRecord`, infoRecord)
        }
        return overridden;
    }, [infoRecord]);

    const watchKeys = useMemo(() => {
        let watchkeys = [
            ...new Set(component.listFieldKeys.filter(f => f.dataGrid).map(f => f.dataGrid!)),
            ...component.listFieldKeys.filter(f => !f.dataGrid).map(f => f.fieldKey),
        ];
        if (log(component)) {
            console.log(`${fieldToLog} watchKeys `, watchkeys)
        }
        return watchkeys;
    }, [component.listFieldKeys, component.fieldKey]);

    const trigger = useWatch({control, name: watchKeys});
    const throttledTrigger = useThrottled(trigger, 150);

    const calculatedValue = useMemo(
        () =>{
            let value = null;
            try {
                value = calculateValue(component, getValues)
            }
            catch (error) {
                console.log(`${fieldToLog} error calculating value `, error)
            }
            if (log(component)) {
                console.log(`throttled trigger usememo ${fieldToLog} calculatedValue `, value)
            }
            return value;
        },
        [throttledTrigger]
    );

    // Always keep the "calculated" info entry current so the tooltip is always accurate
    useEffect(() => {
        if (log(component)) {
            console.log(`${fieldToLog} calculatedValue Changed `, calculatedValue)
        }
        if (calculatedValue === null) {
            if (log(component)) {
                console.log(`${fieldToLog} calculatedValue is null, not setting info`, calculatedValue)
            }
            return;
        }
        setInfo(component.fieldKey, {type: INFO_TYPE.CALCULATED, content: String(calculatedValue)});
        if (log(component)) {
            console.log(`${fieldToLog} setting info to new calculated value`, calculatedValue)
        }
    }, [calculatedValue]);

    // When not overridden, follow the calculation
    useEffect(() => {
        if (log(component)) {
            console.log(`${fieldToLog} calculatedValue ${calculatedValue} or isOverriden ${isOverridden}`)
        }
        if (isOverridden) {
            if (log(component)) {
                console.log(`${fieldToLog} isOverriden ${isOverridden} so we are not changing value on field!`);
            }
            return;
        }
        if (isEqual(field.value, calculatedValue)) {
            if (log(component)) {
                console.log(`${fieldToLog} current value on field is already equal to calculatedValue, no need to update!`);
            }
            return;
        }
        field.onChange(calculatedValue);
        if (log(component)) {
            console.log(`${fieldToLog} should have changed the value to ${calculatedValue}`)
        }
    }, [calculatedValue, isOverridden, field.value]);

    // User edits → write an "override" entry into the info record to signal override state
    const handleChange = useCallback((value: any) => {
        const info = {
            type: INFO_TYPE.OVERRIDE,
            content: String(calculatedValue ?? ""),
        }
        setInfo(component.fieldKey, info);
        field.onChange(value);
        if (log(component)) {
            console.log(`derived ${fieldToLog} changed value manually to ${value}, setInfo to `, info);
        }
    }, [component.fieldKey, calculatedValue, field, setInfo]);

    // Reset is handled entirely by FieldInfoIcon calling clearInfo("override"),
    // which flips isOverridden → false → the useEffect above writes calculatedValue back

    return (
        <NumberField
            parentController={{field: {...field, onChange: handleChange}, fieldState}}
            component={{
                type: FormType.NUMBER,
                fieldKey: component.fieldKey,
                label: component.label,
                compactUI: component.compactUI,
                width: component.width,
                validationRules: component.validationRules,
                suffix: component.suffix,
                formatNumberAs: component.formatNumberAs,
                fieldsToTriggerOnBlur: component.fieldsToTriggerOnBlur,
            }}
        />
    );
};

export const OverrideableNumberCalculation = React.memo(OverrideableNumberCalculationRenderer);
